/**
 * Uploading an authority file: pick, read, confirm.
 *
 * Deliberately two steps with a preview between them, the same shape as the
 * client Excel intake. A VAT filing recorded against the wrong month is worse
 * than no record, and the file itself is the only thing that knows which month it
 * covers — so the office confirms what was read rather than typing it.
 *
 * The preview is a courtesy. What gets stored comes from the server parsing the
 * bytes again, so nothing shown here is load-bearing.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AUTHORITIES, formatIls, formatMonthName, type AuthorityKey } from "@onecpa/shared";
import { Badge, Button, Checkbox, Dialog, Field, Input, Select, Spinner } from "@onecpa/ui";
import { api, type AuthorityFilePreview, type AuthorityParse } from "../api/client.js";
import { useSession } from "../app/session.js";

interface ClientOption {
  id: string;
  name: string;
}

export interface AuthorityFileUploadProps {
  open: boolean;
  onClose(): void;
  clients: ClientOption[];
  /** Pre-selects the client when opened from a matrix row. */
  clientId?: string | undefined;
  /**
   * The period the filing landed in, which is read from the file and is often
   * not the month on screen. Without this the office uploads a September return
   * in November and the list they are looking at does not change.
   */
  onStored?: ((period: { year: number; month: number }) => void) | undefined;
}

export function AuthorityFileUpload({
  open,
  onClose,
  clients,
  clientId,
  onStored,
}: AuthorityFileUploadProps) {
  const session = useSession();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<AuthorityFilePreview | null>(null);
  const [selectedClient, setSelectedClient] = useState(clientId ?? "");
  const [deriveAdvance, setDeriveAdvance] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");

  function reset(): void {
    setFile(null);
    setPreview(null);
    setSelectedClient(clientId ?? "");
    setDeriveAdvance(false);
    setAdvanceAmount("");
  }

  const read = useMutation({
    mutationFn: (chosen: File) => api.reporting.previewAuthorityFile(session.orgId, chosen),
    onSuccess: (result) => {
      setPreview(result);
      // The file names the עוסק, so the usual case is confirming a match rather
      // than searching a list of a hundred and fifty.
      if (result.suggestedClient && !clientId) setSelectedClient(result.suggestedClient.id);
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const ingest = useMutation({
    mutationFn: () =>
      api.reporting.ingestAuthorityFile(session.orgId, {
        file: file!,
        clientId: selectedClient,
        deriveAdvance,
        ...(advanceAmount !== "" ? { advanceAmount: Number(advanceAmount) } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["authority-reports"] });
      await queryClient.invalidateQueries({ queryKey: ["reporting"] });
      if (result.parse.period) onStored?.(result.parse.period);
      session.toast(
        result.report?.status === "error"
          ? "הקובץ נשמר עם שגיאות ולידציה"
          : "הקובץ נקלט ועבר ולידציה",
        result.report?.status === "error" ? "danger" : "success",
      );
      reset();
      onClose();
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  function choose(chosen: File | null): void {
    setFile(chosen);
    setPreview(null);
    if (chosen) read.mutate(chosen);
  }

  const canSubmit = Boolean(file && preview?.parse.period && selectedClient) && !ingest.isPending;

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="העלאת קובץ לרשות"
      description='קובץ מע"מ מ-Finbot (CSV) או PCN874'
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            ביטול
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={ingest.isPending}
            onClick={() => ingest.mutate()}
          >
            שמירת הדוח
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* The native control's button text is set by the browser and comes out
            as "Choose file" in the middle of a Hebrew form. Hiding the input and
            labelling it ourselves is the only way to say בחירת קובץ, and the
            label keeps it keyboard-operable. */}
        <Field label="קובץ" required help={file ? file.name : "CSV או TXT, עד 5MB"}>
          {({ id, describedBy }) => (
            <>
              <input
                id={id}
                aria-describedby={describedBy}
                type="file"
                accept=".csv,.txt,.dat,text/csv,text/plain"
                onChange={(event) => choose(event.target.files?.[0] ?? null)}
                className="peer sr-only"
              />
              <label
                htmlFor={id}
                className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-card border border-rule-strong bg-surface px-3 py-1.5 text-sm text-ink transition-colors hover:border-ink-faint peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-1 peer-focus-visible:outline-ledger"
              >
                בחירת קובץ
              </label>
            </>
          )}
        </Field>

        {read.isPending && (
          <p className="flex items-center gap-2 text-xs text-ink-muted">
            <Spinner /> קורא את הקובץ…
          </p>
        )}

        {preview && <ParsedSummary preview={preview} />}

        {preview?.parse.period && (
          <>
            <Field
              label="לקוח"
              required
              help={
                preview.compId
                  ? `מספר העוסק בקובץ: ${preview.compId}`
                  : "הקובץ אינו נושא מספר עוסק — יש לבחור ידנית"
              }
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={selectedClient}
                  onChange={(event) => setSelectedClient(event.target.value)}
                >
                  <option value="">בחירת לקוח…</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {preview.detected === "vat" && (
              <div className="ruled bg-paper p-3">
                <Checkbox
                  label="יצירת מקדמת מס הכנסה מאותו קובץ"
                  checked={deriveAdvance}
                  onChange={(event) => setDeriveAdvance(event.target.checked)}
                />
                <p className="mt-1.5 text-2xs text-ink-faint">
                  בסיס ההכנסה נגזר מהמחזור שבקובץ. שיעור המקדמה נקבע פרטנית מול פקיד השומה ואינו
                  מופיע באף קובץ, ולכן יש להזין את הסכום.
                </p>
                {deriveAdvance && (
                  <div className="mt-3">
                    <Field label="סכום המקדמה (₪)">
                      {({ id }) => (
                        <Input
                          id={id}
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={advanceAmount}
                          onChange={(event) => setAdvanceAmount(event.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * What the file said.
 *
 * Errors and warnings are kept visually apart because they mean different
 * things: an error means this cannot be filed, a warning means file it but look
 * at this first. Collapsing them into one list is how a real problem gets
 * skimmed past.
 */
function ParsedSummary({ preview }: { preview: AuthorityFilePreview }) {
  const { parse } = preview;

  return (
    <div className="ruled bg-paper">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-rule px-3 py-2.5">
        <Badge tone="info">
          {AUTHORITIES[parse.authority as AuthorityKey]?.label ?? parse.authority}
        </Badge>
        {parse.period ? (
          <span className="text-xs text-ink">
            {formatMonthName(parse.period.month)} {parse.period.year}
            {parse.isBiMonthly && <span className="text-ink-faint"> · דו-חודשי</span>}
          </span>
        ) : (
          <span className="text-xs text-red-ink">התקופה לא נקראה</span>
        )}
        <bdi className="ms-auto truncate text-2xs text-ink-faint">{preview.fileName}</bdi>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-3 sm:grid-cols-3">
        {summaryFacts(parse).map((fact) => (
          <div key={fact.label}>
            <dt className="text-2xs text-ink-faint">{fact.label}</dt>
            {/* Serif is for money, per the design guide. A tax file number and a
                row count are identifiers and counts, not figures. */}
            <dd className={`tnum text-base text-ink ${fact.money ? "serif" : ""}`}>
              <bdi>{fact.value}</bdi>
            </dd>
          </div>
        ))}
      </dl>

      {(parse.errors.length > 0 || parse.warnings.length > 0) && (
        <div className="flex flex-col gap-1.5 border-t border-rule px-3 py-2.5">
          {parse.errors.map((message) => (
            <p key={message} className="text-xs text-red-ink">
              {message}
            </p>
          ))}
          {parse.warnings.map((message) => (
            <p key={message} className="text-xs text-ochre">
              {message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

interface SummaryFact {
  label: string;
  value: string;
  /** Money gets the serif face; identifiers and counts do not. */
  money?: boolean;
}

function summaryFacts(parse: AuthorityParse): SummaryFact[] {
  const summary = parse.summary;
  const text = (key: string): string => {
    const value = summary[key];
    return typeof value === "string" || typeof value === "number" ? String(value) : "—";
  };
  const shekels = (key: string): string => {
    const value = summary[key];
    return typeof value === "number" ? formatIls(value) : "—";
  };
  const fromAgorot = (key: string): string => {
    const value = summary[key];
    return typeof value === "number" ? formatIls(value / 100) : "—";
  };

  if (parse.authority === "vat_detailed") {
    return [
      { label: "מספר עוסק", value: text("compId") },
      { label: "חשבוניות", value: text("invoiceCount") },
      { label: "תנועות", value: text("transactionCount") },
      { label: "סה״כ מחזור", value: fromAgorot("totalAmountAgorot"), money: true },
      { label: 'סה״כ מע"מ', value: fromAgorot("totalVatAgorot"), money: true },
      { label: "קובץ שלם", value: summary["complete"] === true ? "כן" : "לא" },
    ];
  }

  if (parse.authority === "income_tax_advance") {
    return [
      { label: "בסיס הכנסה", value: shekels("incomeBase"), money: true },
      { label: "סכום לתשלום", value: formatIls(parse.amountToPay), money: true },
      { label: "חולק ל-2", value: summary["halvedForCadenceMismatch"] === true ? "כן" : "לא" },
    ];
  }

  return [
    { label: "מספר עוסק", value: text("compId") },
    { label: 'מחזור ללא מע"מ', value: shekels("incomeExcludingVat"), money: true },
    { label: "מחזור פטור", value: shekels("incomeNotSubjectToVat"), money: true },
    { label: 'מע"מ עסקאות', value: shekels("vatDue"), money: true },
    { label: "לתשלום", value: formatIls(parse.amountToPay), money: true },
    {
      label: "תשומות 17% / 18%",
      value: `${shekels("inputVatAssets17")} / ${shekels("inputVatAssets18")}`,
      money: true,
    },
  ];
}
